import React from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { X } from "lucide-react";
import { useClientSignup } from "../hooks/useClientAuth";
import { useClientAuth } from "../ClientAuthContext";
import { useBookingDraft } from "../../booking/BookingDraftContext";
import { useTranslation } from "react-i18next";

interface Props {
    onClose: () => void;
    onSwitchToLogin: () => void;
}

// Validation helpers
function isValidPhone(phone: string): boolean {
    // Must start with +, no whitespace, at least 5 digits
    if (!phone.startsWith("+")) return false;
    if (/\s/.test(phone)) return false;
    const digitsOnly = phone.replace(/\D/g, "");
    return digitsOnly.length >= 5;
}

function isValidEmail(email: string): boolean {
    if (!email) return true; // Optional field
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
}

export default function ClientSignupView({ onClose, onSwitchToLogin }: Props) {
    const { t } = useTranslation();
    const [name, setName] = React.useState("");
    const [phone, setPhone] = React.useState("");
    const [email, setEmail] = React.useState("");
    const [password, setPassword] = React.useState("");
    const [error, setError] = React.useState<string | null>(null);
    const [touched, setTouched] = React.useState<{ [key: string]: boolean }>({});

    const signupMutation = useClientSignup();
    const { actions } = useClientAuth();
    const { draft } = useBookingDraft();
    const companyId = draft.companyId ?? 0;

    // Validation state
    const nameError = touched.name && !name.trim() ? t("widget.common.validation.name_required", "Name is required") : null;
    const phoneError = touched.phone && !isValidPhone(phone)
        ? t("widget.client.signup.phone_invalid", "Phone must start with +, contain no spaces, and include at least 5 digits")
        : null;
    const emailError = touched.email && !isValidEmail(email)
        ? t("widget.common.validation.email_invalid", "Invalid email")
        : null;

    const isFormValid = name.trim() && isValidPhone(phone) && isValidEmail(email);

    const handleSignup = async () => {
        // Mark all fields as touched
        setTouched({ name: true, phone: true, email: true, password: true });

        if (!isFormValid) {
            return;
        }

        setError(null);
        try {
            const res = await signupMutation.mutateAsync({
                phone,
                company_id: companyId,
                name: name.trim(),
                email: email || undefined,
                password: password || undefined,
            });
            actions.login({
                accessToken: res.access_token,
                clientId: res.client_id,
                phone: res.phone,
                expiresAt: Date.now() + res.expires_in * 1000,
                isVerified: res.is_verified,
                name: res.name,
            });
        } catch (e: any) {
            const msg = e?.response?.data?.detail || e?.message || t("widget.client.signup.error", "Registration error");
            setError(msg);
        }
    };

    return (
        <div className="bg-background h-[100dvh] min-h-[100dvh]">
            <div className="w-full h-full mx-auto sm:max-w-[420px] md:max-w-[520px]">
                <Card className="bg-card ios-shadow border-0 h-full flex flex-col sm:rounded-2xl sm:overflow-hidden">
                    {/* Header */}
                    <div className="p-4 border-b border-border/10 flex items-center justify-between">
                        <h2 className="text-lg font-semibold">{t("widget.client.signup.title", "Sign up")}</h2>
                        <Button variant="ghost" size="icon" onClick={onClose}>
                            <X className="w-5 h-5" />
                        </Button>
                    </div>

                    {/* Body */}
                    <div className="flex-1 overflow-y-auto p-6 space-y-4">
                        {/* Platform migration notice */}
                        <div className="rounded-xl border border-rose-200 bg-rose-50 p-3 text-sm text-rose-700 mb-4">
                            {t("widget.client.platform_notice", "We now use the new Boona online booking platform. You need to register again.")}
                        </div>

                        {/* Tabs */}
                        <div className="flex gap-2 mb-4">
                            <Button variant="outline" className="flex-1 rounded-full" onClick={onSwitchToLogin}>
                                {t("widget.client.login.tab", "Log in")}
                            </Button>
                            <Button variant="default" className="flex-1 rounded-full">{t("widget.client.signup.tab", "Sign up")}</Button>
                        </div>

                        <div className="space-y-3">
                            <div className="space-y-1.5">
                                <Label className="flex gap-1 text-xs text-muted-foreground">
                                    {t("widget.common.name", "Name")} <span className="text-destructive">*</span>
                                </Label>
                                <Input
                                    placeholder={t("widget.common.name_placeholder", "Your name")}
                                    value={name}
                                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => setName(e.target.value)}
                                    onBlur={() => setTouched(t => ({ ...t, name: true }))}
                                    className={nameError ? "border-red-500" : ""}
                                />
                                {nameError && <p className="text-xs text-red-500">{nameError}</p>}
                            </div>

                            <div className="space-y-1.5">
                                <Label className="flex gap-1 text-xs text-muted-foreground">
                                    {t("widget.common.phone", "Phone")} <span className="text-destructive">*</span>
                                </Label>
                                <Input
                                    type="tel"
                                    placeholder="+375291234567"
                                    value={phone}
                                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => setPhone(e.target.value)}
                                    onBlur={() => setTouched(t => ({ ...t, phone: true }))}
                                    className={phoneError ? "border-red-500" : ""}
                                />
                                {phoneError && <p className="text-xs text-red-500">{phoneError}</p>}
                            </div>

                            <div className="space-y-1.5">
                                <Label className="text-xs text-muted-foreground">{t("widget.common.email", "Email")}</Label>
                                <Input
                                    type="email"
                                    placeholder={t("widget.common.email_placeholder", "example@email.com")}
                                    value={email}
                                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => setEmail(e.target.value)}
                                    onBlur={() => setTouched(t => ({ ...t, email: true }))}
                                    className={emailError ? "border-red-500" : ""}
                                />
                                <p className="text-xs text-muted-foreground/70 italic">
                                    {t("widget.common.email_hint", "Receive booking details and reminders by email.")}
                                </p>
                                {emailError && <p className="text-xs text-red-500">{emailError}</p>}
                            </div>

                            <div className="space-y-1.5">
                                <Label className="text-xs text-muted-foreground">{t("widget.common.password", "Password")}</Label>
                                <Input
                                    type="password"
                                    placeholder={t("widget.client.signup.password_placeholder", "Minimum 6 characters")}
                                    value={password}
                                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => setPassword(e.target.value)}
                                />
                            </div>

                            {error && (
                                <div className="rounded-xl border border-red-200 bg-red-50 p-3 text-sm text-red-800">
                                    {error}
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Footer CTA */}
                    <div
                        className="sticky bottom-0 inset-x-0 z-20 border-t border-border/10 bg-card/95 backdrop-blur"
                        style={{ paddingBottom: "max(env(safe-area-inset-bottom), 16px)" }}
                    >
                        <div className="p-4 pt-3">
                            <Button
                                className="w-full h-12 rounded-xl font-semibold"
                                onClick={handleSignup}
                                disabled={signupMutation.isPending || !isFormValid}
                            >
                                {signupMutation.isPending ? t("widget.client.signup.submitting", "Signing up...") : t("widget.client.signup.submit", "Sign up")}
                            </Button>
                        </div>
                    </div>
                </Card>
            </div>
        </div>
    );
}
